import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAgentsStore } from '../../stores/agentsStore';
import type {
  AgentPolarity,
  AgentPR,
  AgentRole,
  AgentSummary,
  EmailThread,
} from '../../types/agents';
import { MarkdownView } from './MarkdownView';

interface AgentCardProps {
  agent: AgentSummary;
  prs: AgentPR[];
  emails: EmailThread[];
}

const ROLE_COLORS: Record<AgentRole, string> = {
  producer: 'success.main',
  synthesizer: 'info.main',
  consumer: 'primary.main',
};

const POLARITY_COLORS: Record<AgentPolarity, string> = {
  add: 'success.main',
  subtract: 'warning.main',
  qualitative: 'info.main',
  external: 'secondary.main',
  code: 'primary.main',
};

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const minutes = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

export function AgentCard({ agent, prs, emails }: AgentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [openFindings, setOpenFindings] = useState<string | null>(null);
  const {
    cycleLogs,
    cycleLogLoading,
    loadCycleLog,
    findingsDocs,
    findingsLoading,
    loadFindingsDoc,
  } = useAgentsStore();

  const log = cycleLogs[agent.name];
  const logLoading = cycleLogLoading[agent.name];
  const ownPRs = prs.filter((pr) => pr.agent === agent.name).slice(0, 5);
  const ownEmails = agent.name === 'user-pulse' ? emails.slice(0, 5) : ([] as EmailThread[]);
  // After PR-3 of #777 the pr-agent special case is gone — every agent renders
  // through the same cycle-log + findings + PRs surface. pr-agent's cycle-log
  // is populated by its own runtime per docs/loop/agents/pr-agent/prompt.md.
  const hasNoFireYet = !agent.has_cycle_log;

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !log && !logLoading) {
      void loadCycleLog(agent.name);
    }
  };

  const toggleFindings = (filename: string) => {
    if (openFindings === filename) {
      setOpenFindings(null);
      return;
    }
    setOpenFindings(filename);
    if (!findingsDocs[filename] && !findingsLoading[filename]) {
      void loadFindingsDoc(filename);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`agent-card-${agent.name}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: ROLE_COLORS[agent.role],
          }}
        />
        <Typography variant="h6" sx={{ fontFamily: 'monospace', flex: 1 }}>
          {agent.name}
        </Typography>
        <Chip
          label={agent.role}
          size="small"
          sx={{ bgcolor: ROLE_COLORS[agent.role], color: 'common.white' }}
        />
        <Chip
          label={agent.polarity}
          size="small"
          variant="outlined"
          sx={{ borderColor: POLARITY_COLORS[agent.polarity] }}
        />
        {agent.open_issue_count > 0 && (
          <Chip
            label={`${agent.open_issue_count} open`}
            size="small"
            color={agent.open_issue_count >= 10 ? 'warning' : 'default'}
          />
        )}
        <IconButton size="small" onClick={handleToggle}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {hasNoFireYet ? (
        <Typography variant="body2" color="text.secondary">
          No cycles recorded yet.
        </Typography>
      ) : (
        <>
          <Typography
            variant="body2"
            sx={{
              fontStyle: agent.last_skipped ? 'italic' : 'normal',
              color: agent.last_skipped ? 'text.secondary' : 'text.primary',
            }}
          >
            <strong>Last fire:</strong> {formatRelative(agent.last_fire)} —{' '}
            {agent.last_action ?? '—'}
          </Typography>
          {agent.latest_findings && (
            <Box sx={{ mt: 1 }}>
              <Link
                component="button"
                onClick={() => toggleFindings(agent.latest_findings!.filename)}
                sx={{ textAlign: 'left' }}
              >
                Latest findings: {agent.latest_findings.filename} ({agent.latest_findings.size_kb}
                KB)
              </Link>
              <Collapse in={openFindings === agent.latest_findings.filename}>
                <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: 'grey.50' }}>
                  {findingsLoading[agent.latest_findings.filename] ? (
                    <CircularProgress size={20} />
                  ) : findingsDocs[agent.latest_findings.filename] ? (
                    <MarkdownView
                      body={findingsDocs[agent.latest_findings.filename].body}
                      maxHeight={500}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No content loaded.
                    </Typography>
                  )}
                </Paper>
              </Collapse>
            </Box>
          )}
          {agent.name === 'user-pulse' && ownEmails.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Recent email threads:
              </Typography>
              {ownEmails.map((email) => (
                <Box key={email.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={email.direction} size="small" variant="outlined" />
                  {email.status === 'failed' && (
                    <Chip label="failed" size="small" color="error" variant="outlined" />
                  )}
                  <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                    {email.subject}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Recent cycle-log entries
          </Typography>
          {logLoading ? (
            <CircularProgress size={20} sx={{ mt: 1 }} />
          ) : log && log.length > 0 ? (
            <Box sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
              {log.slice(0, 20).map((entry, idx) => (
                <Box
                  key={`${entry.timestamp}-${idx}`}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: entry.skipped ? 'text.secondary' : 'text.primary',
                    fontStyle: entry.skipped ? 'italic' : 'normal',
                    py: 0.25,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {entry.timestamp.replace('T', ' ').replace('Z', '')} — {entry.action}
                  {Object.keys(entry.fields).length > 0 && (
                    <Box component="span" sx={{ color: 'text.disabled', ml: 1 }}>
                      ·{' '}
                      {Object.entries(entry.fields)
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ')}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No entries.
            </Typography>
          )}
          {ownPRs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Recent PRs
              </Typography>
              {ownPRs.map((pr) => (
                <Box key={pr.number} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Link href={pr.url} target="_blank" rel="noreferrer">
                    #{pr.number} <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                  <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                    {pr.title}
                  </Typography>
                  <Chip label={pr.state} size="small" variant="outlined" />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
